import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiShield, FiLock, FiEye, FiServer, FiMail, FiGlobe, FiDatabase, FiCheckCircle } from 'react-icons/fi';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';

const Section = ({ icon: Icon, title, children }) => (
    <motion.section
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="relative group block p-8 rounded-3xl bg-white/[0.02] border border-white/[0.05] hover:border-blue-500/30 transition-all duration-500 shadow-2xl hover:bg-white/[0.04]"
    >
        <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center text-blue-500 group-hover:bg-blue-600 group-hover:text-white transition-all duration-500">
                <Icon size={24} />
            </div>
            <h2 className="text-2xl font-black text-white tracking-tight uppercase tracking-widest leading-none">{title}</h2>
        </div>
        <div className="text-neutral-400 leading-relaxed space-y-4 font-medium">
            {children}
        </div>
    </motion.section>
);

export default function Privacy() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen bg-[#050505] flex flex-col selection:bg-blue-500/30 selection:text-white">
            <Navbar />

            <main className="flex-grow pt-40 pb-32 px-6 relative overflow-hidden">
                {/* Background Glows */}
                <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[120px] -z-10"></div>
                <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-blue-600/5 rounded-full blur-[120px] -z-10"></div>

                <div className="max-w-5xl mx-auto">
                    {/* Hero Header */}
                    <div className="text-center mb-24 max-w-2xl mx-auto">
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-500 text-xs font-black uppercase tracking-[0.2em] mb-8"
                        >
                            <FiShield size={14} /> Compliance Protocol
                        </motion.div>
                        <h1 className="text-5xl md:text-6xl font-black text-white mb-6 tracking-tighter leading-tight">
                            Privacy <span className="text-blue-600 italic">Protection</span>
                        </h1>
                        <p className="text-neutral-500 font-bold tracking-widest uppercase text-xs">
                            System Version A.10 • Last Synchronized: Jan 2026
                        </p>
                    </div>

                    {/* Content Grid */}
                    <div className="space-y-8">
                        <Section icon={FiEye} title="Intelligence Collection">
                            <p>
                                At <span className="font-bold text-white">Maths with Manoj</span>, data integrity is paramount. We collect specific data points to optimize your learning trajectory:
                            </p>
                            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                                {[
                                    'Biometric Verification (Identity Verification)',
                                    'Academic Level & Grade Stream',
                                    'Financial Transaction Logs (Encrypted Slips)',
                                    'Geo-Spatial Delivery Coordinates',
                                    'Session Engagement Analytics',
                                    'Educational Background Data'
                                ].map((item, i) => (
                                    <li key={i} className="flex items-center gap-3 p-4 rounded-2xl bg-white/[0.03] border border-white/[0.05] hover:border-blue-500/30 transition-all group">
                                        <FiCheckCircle className="text-blue-500 shrink-0 group-hover:scale-110 transition-transform" />
                                        <span className="text-sm font-bold text-neutral-300">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </Section>

                        <Section icon={FiLock} title="Usage Architecture">
                            <p>Your data fuels our educational engine. We utilize collected information for:</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4">
                                <div className="space-y-3 p-6 rounded-2xl bg-white/[0.02] border border-white/[0.05]">
                                    <h4 className="text-[10px] font-black text-blue-500 uppercase tracking-[0.2em]">Operational Core</h4>
                                    <p className="text-sm text-neutral-400">Enabling access to proprietary video archives and live stream components, ensuring seamless enrollment and content delivery.</p>
                                </div>
                                <div className="space-y-3 p-6 rounded-2xl bg-white/[0.02] border border-white/[0.05]">
                                    <h4 className="text-[10px] font-black text-blue-500 uppercase tracking-[0.2em]">Security Layer</h4>
                                    <p className="text-sm text-neutral-400">Verifying payment authenticity and protecting intellectual property through dynamic watermarking and device tracking.</p>
                                </div>
                            </div>
                        </Section>

                        <Section icon={FiServer} title="Data Custody">
                            <p>
                                We implement military-grade security protocols to protect your academic footprint. Data is encrypted at rest and in transit using SSL/TLS engineering.
                            </p>
                            <div className="bg-[#0a0a0a] text-blue-400 p-8 rounded-[2rem] font-mono text-xs overflow-hidden relative shadow-inner border border-white/[0.05]">
                                <div className="absolute top-0 right-0 p-4 opacity-10"><FiDatabase size={60} /></div>
                                <div className="flex gap-2 items-center mb-6 text-white/40 border-b border-white/5 pb-4 uppercase tracking-widest font-black italic text-[10px]">
                                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.6)]"></div>
                                    Storage encryption: ACTIVE
                                </div>
                                <p className="text-blue-500/60">// PAYMENT SLIP ENCRYPTION PROTOCOL</p>
                                <p className="mt-4 text-white/70 leading-relaxed font-bold">ALL SENSITIVE ASSETS ARE HASHED AND STORED IN ISOLATED ENVIRONMENTS. ACCESS IS RESTRICTED TO AUDITED ACADEMIC COORDINATORS.</p>
                            </div>
                        </Section>

                        <Section icon={FiGlobe} title="Tracking & Cookies">
                            <p>
                                We use tactical cookies to maintain session states and analyze laboratory performance metrics. These small data fragments ensure that your progress is synchronized across your primary learning devices.
                            </p>
                        </Section>

                        {/* Contact Card */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            className="p-16 rounded-[3rem] bg-white/[0.02] border border-white/[0.05] text-white relative overflow-hidden text-center shadow-2xl"
                        >
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-transparent pointer-events-none"></div>
                            <h2 className="text-4xl font-black mb-6 tracking-tighter">Need Support?</h2>
                            <p className="text-neutral-500 font-bold mb-12 max-w-xl mx-auto uppercase tracking-widest text-[10px]">
                                If you have queries concerning our privacy framework, our coordination team is available to assist.
                            </p>
                            <div className="flex flex-col md:flex-row justify-center gap-8 items-center">
                                <div className="flex items-center gap-5 text-left p-6 rounded-[1.5rem] bg-white/[0.03] border border-white/[0.05] w-full md:w-auto hover:bg-white/[0.05] transition-colors">
                                    <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-500">
                                        <FiMail size={24} />
                                    </div>
                                    <div>
                                        <p className="text-[9px] font-black uppercase text-neutral-600 tracking-widest mb-1">Email Hub</p>
                                        <p className="text-sm font-black text-white">support@mathswithmanoj.com</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-5 text-left p-6 rounded-[1.5rem] bg-white/[0.03] border border-white/[0.05] w-full md:w-auto hover:bg-white/[0.05] transition-colors">
                                    <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-500">
                                        <FiGlobe size={24} />
                                    </div>
                                    <div>
                                        <p className="text-[9px] font-black uppercase text-neutral-600 tracking-widest mb-1">Official Web</p>
                                        <p className="text-sm font-black text-white">www.mathswithmanoj.com</p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
}
